/**
 * Helpers de persistência de etapas do funil (save / delete / refresh).
 */
import type { FunnelStage } from '../../../../types';
import type { FunnelStageData } from '../components/FunnelStageRow';

export type SaveFunnelStageFn = (stage: FunnelStage) => Promise<void>;
export type DeleteFunnelStageFn = (stageId: string) => Promise<void>;
export type RefreshStagesFn = (funnelId?: string) => Promise<void>;
export type RefreshLeadsFn = (funnelId?: string | null) => Promise<void>;

export const tempStageId = (suffix: string | number = Math.random()) =>
    `temp_${Date.now()}_${suffix}`;

export function keptPersistedStageIds(stages: FunnelStageData[]): string[] {
    return stages
        .map((s) => s.id)
        .filter((id): id is string => Boolean(id && !id.startsWith('temp_')));
}

export async function refreshFunnelData(
    refreshStages: RefreshStagesFn,
    refreshLeads: RefreshLeadsFn,
    funnelId: string,
) {
    await refreshStages(funnelId);
    await refreshLeads(funnelId);
}

export async function persistStages(
    saveFunnelStage: SaveFunnelStageFn,
    funnelId: string,
    stages: FunnelStageData[],
    resolveId: (stage: FunnelStageData, order: number) => string,
) {
    let order = 0;
    for (const stage of stages) {
        await saveFunnelStage({
            id: resolveId(stage, order),
            name: stage.name,
            deadline: stage.deadline,
            conversionEvent: stage.conversionEvent,
            color: stage.color || null,
            funnelId,
            order: order++,
        });
    }
}

export async function deleteRemovedStages(
    deleteFunnelStage: DeleteFunnelStageFn,
    dbStages: Array<{ id: string }>,
    formStages: FunnelStageData[],
) {
    const keptIds = keptPersistedStageIds(formStages);
    for (const stage of dbStages.filter((s) => !keptIds.includes(s.id))) {
        await deleteFunnelStage(stage.id);
    }
}

/** Resolve id para create/edit: mantém persistido ou gera temp. */
export function resolvePersistedOrTempId(stage: FunnelStageData, order?: number) {
    if (stage.id && !stage.id.startsWith('temp_')) return stage.id;
    return tempStageId(order ?? Math.random());
}
